import React from 'react'
import PropTypes from 'prop-types'
import {
  ToastContainer,
  cssTransition,
  toast as toastify,
} from 'react-toastify/unstyled'

import { Action } from '../buttons'
import { combineClasses } from '~/util'

import styles from './Toasts.module.scss'
import './Toasts.animations.scss'

import XIcon from '~/assets/icons/x.svg?react'

const DEFAULT_DURATION = 3000

/*
 * `react-toastify` drives its lifecycle off `animationend`: the auto-close
 * timer does not start until the enter animation finishes, and a dismissed
 * toast is not removed from the DOM until the exit animation finishes. The
 * `unstyled` entry point ships no CSS, so these keyframes are required for
 * toasts to dismiss at all - see `Toasts.animations.scss`.
 */
const ToastTransition = cssTransition({
  enter: 'toast-enter',
  exit: 'toast-exit',
})

/**
 * The container that all toasts are rendered into. It must be mounted once,
 * near the root of the app, or calls to `toast.*` will not display anything.
 *
 * We use the `unstyled` entry point of `react-toastify` so that none of its
 * CSS is injected; toast styling lives in `Toasts.module.scss` and the
 * lifecycle keyframes in `Toasts.animations.scss`. Nothing of the library's
 * own chrome is rendered - the close button is ours, the progress bar is
 * hidden, and the bodies are the `*Toast` components below - so its default
 * stylesheet would be near-entirely dead CSS, and the rest would sit on
 * global class names at higher specificity than our CSS modules.
 *
 * `pauseOnFocusLoss` is off because freezing the auto-close timer whenever the
 * window loses focus would leave toasts on screen indefinitely.
 */
export function Toasts() {
  return (
    <div id="react-toast">
      <ToastContainer
        position="top-center"
        autoClose={DEFAULT_DURATION}
        transition={ToastTransition}
        hideProgressBar
        newestOnTop
        closeButton={false}
        closeOnClick={false}
        draggable={false}
        pauseOnFocusLoss={false}
        className={styles.Toasts}
        toastClassName={styles.toastWrapper}
      />
    </div>
  )
}

function show(Component, message, duration) {
  return toastify(
    ({ closeToast }) => <Component message={message} onClose={closeToast} />,
    { autoClose: duration }
  )
}

const toast = {
  notify(message, duration = DEFAULT_DURATION) {
    return show(InfoToast, message, duration)
  },

  info(message, duration = DEFAULT_DURATION) {
    return show(InfoToast, message, duration)
  },

  success(message, duration = DEFAULT_DURATION) {
    return show(SuccessToast, message, duration)
  },

  warn(message, duration = DEFAULT_DURATION) {
    return show(WarningToast, message, duration)
  },

  error(message, duration = DEFAULT_DURATION) {
    return show(ErrorToast, message, duration)
  },

  closeAll() {
    toastify.dismiss()
  },
}

export default toast

function Toast({ children, display, onClose, className, ...rest }) {
  return (
    <div className={combineClasses(styles.Toast, className)} {...rest}>
      <div className={styles.message}>{children}</div>
      <Action
        button
        feel="button"
        display={display}
        transparent
        onClick={onClose}
        className={styles.closeButton}
        size="s"
        icon={<XIcon />}
      />
    </div>
  )
}

export function InfoToast({ message, onClose }) {
  return (
    <Toast className={styles.InfoToast} onClose={onClose} data-test="infoToast">
      {message}
    </Toast>
  )
}

InfoToast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export function SuccessToast({ message, onClose }) {
  return (
    <Toast
      className={styles.SuccessToast}
      onClose={onClose}
      data-test="successToast"
      display="success"
    >
      {message}
    </Toast>
  )
}

SuccessToast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export function WarningToast({ message, onClose }) {
  return (
    <Toast
      className={styles.WarningToast}
      onClose={onClose}
      data-test="warningToast"
      display="warn"
    >
      {message}
    </Toast>
  )
}

WarningToast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export function ErrorToast({ message, onClose }) {
  return (
    <Toast
      className={styles.ErrorToast}
      onClose={onClose}
      data-test="errorToast"
      display="error"
    >
      {message}
    </Toast>
  )
}

ErrorToast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}
